import React, { useEffect, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import "./rcaStyle.css";
import "../chainedFiveWhys.css";
import {
  Icon,
  LoadingIndicator,
  Typography,
  Button,
} from "@anchor/react-components/dist/lib/components";
import RciModal from "../RciModal";
import ChainedFiveWhys from "../ChainedFiveWhys";
import ButtonGenerator from "../../ButtonFactory/ButtonGenerator";
import { ChainedFiveWhysService } from "../../../services/chainedFiveWhysService";
import { McTooltip, McIcon, McInput, McTextarea, McButton, McModal, McTabBar, McTab } from "@maersk-global/mds-react-wrapper/components-core";
import { useQueryClient } from "react-query";
import { AiSuggestionsInline } from "../../Report/RcaQualityPanel";
import { useCallback } from "react";
import UserInfo from "../../../context/UserAccessContext";
import { IsLocked } from "../../IsLocked/IsLocked";
import PhotoUploadModal from "../../PhotoUploadModal/PhotoUploadModal";
import UseHandleFileUpload from "../../../Utils/UseHandleFileUpload";
import UploadedImages from "../../Images/UploadedImages";
import PhotoPreviewModal from "../../PhotoPreviewModal/PhotoPreviewModal";
// import { ImageServices } from "../../../services/imageServices"; // Not needed - parent manages fetch
import { useMsal } from "@azure/msal-react";
// import { useWebSocket } from "../../../context/WebSocketContext/WebSocketContext";

const hasRealText = (html) => {
  if (!html) return false;
  return html.replace(/<[^>]*>/g, "").trim().length > 0;
};

// Check if content meets minimum length (50 chars for meaningful content)
const hasMinimumContent = (html, minChars = 50) => {
  if (!html) return false;
  const text = html.replace(/<[^>]*>/g, "").trim();
  return text.length >= minChars;
};

const CAUSE_QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};
const CAUSE_QUILL_FORMATS = ["header", "bold", "italic", "underline", "list", "bullet", "link"];

function RootCauseCauseSection({ cause, dispatch, rcCodeState, lockedFields, postmortemIsIncompleteState, onAutoSave }) {
  const [causeValue, setCauseValue] = useState(cause || "");
  const [modalOpen, setModalOpen] = useState(false);
  const savingRef = useRef(false);
  const { graphData } = UserInfo();

  useEffect(() => { setCauseValue(cause || ""); }, [cause]);

  const handleOpen = () => setModalOpen(true);
  const handleClose = () => setModalOpen(false);

  const onSave = () => {
    savingRef.current = true;
    dispatch({
      type: "HANDLE EDITOR CHANGE",
      field: "RootCauseState",
      payload: { ...rcCodeState, cause: causeValue },
    });
    dispatch({
      type: "HANDLE EDITOR CHANGE",
      field: "changedFileds",
      payload: (prev) => ({ ...prev, cause: true }),
    });
    handleClose();
    if (onAutoSave) {
      setTimeout(() => { onAutoSave(); savingRef.current = false; }, 100);
    } else {
      savingRef.current = false;
    }
  };

  const isLocked = Array.isArray(lockedFields) &&
    lockedFields.find(f => f.fieldName === "cause" && f.lockedBy !== graphData?.username);

  return (
    <>
      <div style={{ margin: "16px 0 0" }} onClick={(e) => e.stopPropagation()}>
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: "11px",
          fontWeight: "700",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--mds_brand_appearance_default_default_text-color-secondary, #6b6b6b)",
          background: "var(--mds_brand_appearance_neutral_weak_default_background-color, #f5f7f8)",
          borderTop: "1px solid var(--mds_brand_appearance_default_border-color, #e0e0e0)",
          borderBottom: "1px solid var(--mds_brand_appearance_default_border-color, #e0e0e0)",
          padding: "8px 16px",
          margin: "0 -16px",
        }}>
          <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            Root Cause
            {hasMinimumContent(causeValue) ? (
              <span style={{
                fontSize: "10px", fontWeight: "600", color: "#1a8a4a",
                background: "#e6f4ec", borderRadius: "10px",
                padding: "1px 8px", letterSpacing: "0.02em", textTransform: "none",
              }}>✓ Complete</span>
            ) : (
              <span style={{
                fontSize: "10px", fontWeight: "600", color: "#b86800",
                background: "#fff", borderRadius: "10px",
                padding: "1px 8px", letterSpacing: "0.02em", textTransform: "none",
              }}>Incomplete</span>
            )}
          </span>
          {!postmortemIsIncompleteState && (
            <button className="sre-section-header-action" onClick={handleOpen}
              style={{ textTransform: "none", letterSpacing: "normal", fontSize: "12px" }}>
              <Icon name="pencil" style={{ fontSize: "12px" }} />
              Edit
            </button>
          )}
        </div>
        <div
          className={!postmortemIsIncompleteState ? "sre-section-body--editable" : undefined}
          style={{ padding: "12px 16px", cursor: !postmortemIsIncompleteState ? "pointer" : undefined }}
          onClick={!postmortemIsIncompleteState ? handleOpen : undefined}
        >
          <div style={{
            fontSize: "14px", lineHeight: 1.7,
            color: "var(--mds_brand_appearance_default_default_text-color, #141414)",
            wordBreak: "break-word",
            ...(isLocked ? { border: "1px solid red", padding: "10px", borderRadius: "5px" } : {}),
          }}>
            <div dangerouslySetInnerHTML={{ __html: causeValue.replace(/\n/g, "<br/>") }} />
          </div>
        </div>
      </div>

      <McModal
        heading="Root Cause"
        open={modalOpen}
        dimension="medium"
        backdropcloseactiondisabled={true}
        escapecloseactiondisabled={true}
        closed={handleClose}
      >
        <div style={{ minHeight: "300px" }}>
          <ReactQuill
            theme="snow"
            modules={CAUSE_QUILL_MODULES}
            formats={CAUSE_QUILL_FORMATS}
            value={causeValue}
            onChange={setCauseValue}
            style={{ background: "#fff", minHeight: "240px" }}
          />
        </div>
        <McButton slot="primaryAction" appearance="primary" click={onSave}>
          Save
        </McButton>
        <McButton slot="secondaryAction" appearance="neutral" variant="outlined" click={handleClose}>
          Cancel
        </McButton>
      </McModal>
    </>
  );
}

function WhyRow({ label, question, answer, isLast }) {
  return (
    <div style={{
      display: "flex",
      gap: "12px",
      padding: "10px 0",
      borderBottom: isLast ? "none" : "1px solid var(--mds_brand_appearance_default_border-color, #e8e8e8)",
    }}>
      <span style={{
        flexShrink: 0, fontSize: "12px", fontWeight: "700",
        color: "var(--mds_brand_appearance_primary_default_text-color, #0073ab)",
        letterSpacing: "0.05em", paddingTop: "2px", minWidth: "40px",
      }}>
        {label}
      </span>
      <div style={{ flex: 1 }}>
        {question && (
          <div style={{ fontSize: "14px", fontWeight: "600", color: "var(--mds_brand_appearance_default_default_text-color, #141414)", marginBottom: "4px" }}>
            {question}
          </div>
        )}
        {answer ? (
          <div style={{ fontSize: "14px", color: "var(--mds_brand_appearance_default_default_text-color, #141414)", lineHeight: 1.6 }}
            dangerouslySetInnerHTML={{ __html: answer }} />
        ) : (
          <span style={{ fontSize: "14px", color: "var(--mds_brand_appearance_default_default_text-color, #141414)", fontStyle: "italic" }}>
            No answer provided
          </span>
        )}
      </div>
    </div>
  );
}


const Loading = () => {
  return (
    <LoadingIndicator
      appearance="default"
      fit="large"
      id="testing"
      label="Fetching images, Please Wait..."
      variant="spinner"
      style={{ background: "none" }}
    />
  );
};

function RootCauseIdentification(props) {
  const [rciModal, setRciModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [aiOpen, setAiOpen] = useState(false); // Track if editing chained 5-whys
  const [chainedData, setChainedData] = useState([]); // Store fetched chained data
  const [loadingChainedData, setLoadingChainedData] = useState(false);
  const rciSavingRef = useRef(false);
  const { graphData } = UserInfo();
  const { instance, accounts } = useMsal();
  const queryClient = useQueryClient();
  const chainedService = ChainedFiveWhysService();
  // const { getImage } = ImageServices(); // Not needed - parent manages fetch

  // const { unlockField } = useWebSocket();
  const unlockField = null; // WebSocket disabled

  // Image upload state
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [photosFor, setPhotosFor] = useState("");
  const [photoModal, setPhotoModal] = useState(false);
  const [imageToPreview, setImageToPreview] = useState(null);
  const [previewModal, setPreviewModal] = useState(false);
  const [uploadedRciImages, setUploadedRciImages] = useState([]);
  const [fetchAgain, setFetchAgain] = useState(false);

  // Debug log for fetchAgain changes
  useEffect(() => {
    console.log('[RCI] fetchAgain state changed to:', fetchAgain);
  }, [fetchAgain]);

  const [loadingImage, setLoadingImage] = useState({});
  const [errorInFetchingImages, setErrorInFetchingImages] = useState({});

  // Image upload handlers
  const handlePhotoModalClick = (photoFor) => {
    setPhotosFor(photoFor);
    setPhotoModal(true);
  };

  const handlePhotoModalClose = () => {
    setPhotoModal(false);
  };

  const hanldePreviewModal = () => {
    setPreviewModal(!previewModal);
  };

  const { handleFiles, fileChange, deleteImage, previewImage } =
    UseHandleFileUpload(
      props.images,
      setError,
      setErrorMessage,
      photosFor,
      hanldePreviewModal,
      setImageToPreview,
      props.dispatch,
      props.imageFormData,
      "rootCause",
      props.setFetchAgain,  // Use prop from parent
      props.incidentID
    );

  // Wrap fileChange to close modal after upload
  const fileChangeWithModalClose = async (data) => {
    await fileChange(data);
    // Close modal after successful upload
    setTimeout(() => {
      handlePhotoModalClose();
    }, 500);
  };

  // Images and fetch logic are managed by parent RootCause.jsx
  // Remove duplicate fetchAndSetImages and useEffect

  const handleRciModal = () => {
    setRciModal(!rciModal);
    props.setModalIsOpening((current) => {
      return {
        ...current,
        rootCauseIdentification: !current.rootCauseIdentification,
      };
    });
  };

  // Handle edit button click - simply toggle edit mode
  const handleEditClick = () => {
    setIsEditMode(true);
  };

  // Handle save description only (stays in edit mode)
  const handleSaveDescription = async () => {
    // Save only the description to parent
    await onSave();
  };

  // Handle exit edit mode (chained)
  const handleExitEditMode = async () => {
    setIsEditMode(false);
    await fetchChainedData();
  };

  // Handle case where user deletes all chains — API rejects empty save, so clear locally
  const handleClearAllChains = () => {
    setChainedData([]);
    setIsEditMode(false);
  };

  // Handle save + exit for classic questionnaire edit
  const handleSaveAndExitClassic = async () => {
    await onSave();
    setIsEditMode(false);
  };

  // Fetch chained 5-whys data
  const fetchChainedData = async () => {
    if (!props.incidentID) return;

    setLoadingChainedData(true);
    try {
      const chains = await chainedService.getChainedFiveWhysCached(queryClient, props.incidentID, instance, accounts);
      console.log('[RCI] Fetched chained data:', chains);
      setChainedData(chains || []);
    } catch (error) {
      console.error('[RCI] Error fetching chained data:', error);
      setChainedData([]);
    } finally {
      setLoadingChainedData(false);
    }
  };

  // Fetch chained data when component mounts or incidentID changes
  useEffect(() => {
    if (props.incidentID) {
      fetchChainedData();
    }
  }, [props.incidentID]);

  const getNumberOfQuestions = useCallback(() => {
    let count = 0;
    // Check if questions
    props.RootCauseIdentificationState.questions &&
      props.RootCauseIdentificationState.questions.map((question) => {
        if (question && question.question !== "") {
          count++;
        }
      });

    return count === 0 ? 1 : count;
  }, [props.RootCauseIdentificationState.questions]);

  const [numberOfQuestions, setNumberOfQuestions] = useState(0);
  const deleteQuestion = (index) => {

    if (rciData.questions.length === 1) {
      const dataToBeUpdated = {
        ...rciData,
        questions: [
          {
            question: "",
            answer: "",
          },
        ],
        changedFields: {
          ...rciData.changedFields,
          rciquestions: true,
        },
      };

      setRciData(() => ({ ...dataToBeUpdated }));
      setNumberOfQuestions(1);
      return;
    }

    let currentQuestionaire = rciData.questions;

    let filteredQuestionaire = currentQuestionaire.filter(
      (item, i) => i !== index
    );

    const dataToBeUpdated = {
      ...rciData,
      questions: [...filteredQuestionaire],
      changedFields: {
        ...rciData.changedFields,
        rciquestions: true,
      },
    };

    setRciData(() => ({ ...dataToBeUpdated }));
    if (typeof unlockField === 'function') unlockField("rci_questions", dataToBeUpdated);
    setNumberOfQuestions(numberOfQuestions - 1);
  };

  const initialQuestionaire =
    props.RootCauseIdentificationState.questions || [];
  const [rciData, setRciData] = useState({
    questions: initialQuestionaire,
    description: props.RootCauseIdentificationState.description || "",
    changedFields: { ...props.changedFields },
  });

  useEffect(() => {
    // Check if questions data is available
    if (props.RootCauseIdentificationState.questions) {
      setNumberOfQuestions(getNumberOfQuestions());

      const rawText = props.RootCauseIdentificationState.description || "";

      // Convert plain text newlines to HTML if needed
      const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(rawText);

      let processedText = rawText;
      if (!hasHtmlTags && rawText.includes('\n')) {
        const lines = rawText.split('\n').filter(line => line.trim());
        processedText = lines.map(line => `<p>${line}</p>`).join('');
      } else if (!hasHtmlTags) {
        processedText = rawText.trim() ? `<p>${rawText}</p>` : rawText;
      }

      setRciData((prevState) => ({
        ...prevState,
        description: processedText,
        questions: props.RootCauseIdentificationState.questions,
      }));
    }
  }, [getNumberOfQuestions, props.RootCauseIdentificationState.questions]);

  // Add useEffect to sync changedFields from props
  useEffect(() => {


    setRciData((prevState) => {
      const shouldUpdate =
        props.changedFields &&
        (props.changedFields.rcidescription !== prevState.changedFields.rcidescription ||
          props.changedFields.rciquestions !== prevState.changedFields.rciquestions);

      if (!shouldUpdate) {
        return prevState; // No update needed
      }



      return {
        ...prevState,
        changedFields: {
          ...prevState.changedFields,
          rcidescription: props.changedFields.rcidescription ?? prevState.changedFields.rcidescription,
          rciquestions: props.changedFields.rciquestions ?? prevState.changedFields.rciquestions,
        },
      };
    });
  }, [props.changedFields]);

  const onSave = async () => {
    rciSavingRef.current = true;

    props.dispatch({
      type: "HANDLE EDITOR CHANGE",
      field: "RootCauseIdentificationState",
      payload: {
        questions: rciData.questions,
        description: rciData.description,
      },
    });

    props.dispatch({
      type: "HANDLE EDITOR CHANGE",
      field: "changedFileds",
      payload: (prevChangedFileds) => ({
        ...prevChangedFileds,
        rcidescription: rciData.changedFields.rcidescription || prevChangedFileds.rcidescription,
        rciquestions: rciData.changedFields.rciquestions || prevChangedFileds.rciquestions,
      }),
    });

    // Don't open RciModal - this was for legacy modal-based editing
    // handleRciModal();

    if (props.onAutoSave) {
      setTimeout(async () => {
        await props.onAutoSave();
        rciSavingRef.current = false;
      }, 100);
    } else {
      rciSavingRef.current = false;
    }
  };


  const increaseQuestions = () => {
    setNumberOfQuestions(numberOfQuestions + 1);
    setRciData({
      ...rciData,
      questions: [
        ...rciData.questions,
        {
          question: "",
          answer: "",
        },
      ],
    });
  };

  const handleRciData = (event) => {
    const { name, value } = event.target;

    if (name === "description") {
      setRciData((prevData) => {
        // Only set rcidescription to true if the value actually changed
        const hasChanged = prevData.description !== value;

        return {
          ...prevData,
          [name]: value,
          changedFields: {
            ...prevData.changedFields,
            rcidescription: hasChanged ? true : prevData.changedFields.rcidescription,
          },
        };
      });
      return;
    } else {
      setRciData((prevData) => {
        // Only set rciquestions to true if the value actually changed
        const hasChanged = prevData[name] !== value;

        return {
          ...prevData,
          [name]: value,
          changedFields: {
            ...prevData.changedFields,
            rciquestions: hasChanged ? true : prevData.changedFields.rciquestions,
          },
        };
      });
    }
  };

  const handleRciQuestionaireQuestions = (event, index) => {
    const { name, value } = event.target;

    setRciData((prevData) => {
      // Check if the question actually changed
      const currentQuestion = prevData.questions[index]?.question || "";
      const hasChanged = currentQuestion !== value;

      return {
        ...prevData,
        questions: [
          ...prevData.questions.slice(0, index),
          {
            ...prevData.questions[index],
            question: value,
          },
          ...prevData.questions.slice(index + 1),
        ],
        changedFields: {
          ...prevData.changedFields,
          rciquestions: hasChanged ? true : prevData.changedFields.rciquestions,
        },
      };
    });
  };

  const handleRciQuestionaireAnswers = (event, index) => {
    const { name, value } = event.target;

    setRciData((prevData) => {
      // Check if the answer actually changed
      const currentAnswer = prevData.questions[index]?.answer || "";
      const hasChanged = currentAnswer !== value;

      return {
        ...prevData,
        questions: [
          ...prevData.questions.slice(0, index),
          {
            ...prevData.questions[index],
            answer: value,
          },
          ...prevData.questions.slice(index + 1),
        ],
        changedFields: {
          ...prevData.changedFields,
          rciquestions: hasChanged ? true : prevData.changedFields.rciquestions,
        },
      };
    });
  };

  return (
    <>
      <div id="section-rci" className="sre-section-card rootCause-identification-description-div-tour-step five-why-div-tour-step">
        {/* Header */}
        <div className="sre-section-header">
          <span className="sre-section-header-title">Root Cause Identification</span>
          {(() => {
            // While in edit mode, badge reflects last-saved state (not in-session changes)
            if (isEditMode) {
              return (
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: "4px",
                  fontSize: "10px", fontWeight: "600", letterSpacing: "0.04em",
                  color: "rgba(255,255,255,0.7)", borderRadius: "10px", padding: "2px 8px",
                }}>Editing…</span>
              );
            }
            // During async load, show neutral state
            if (loadingChainedData) {
              return null;
            }
            const hasCompleteChain = chainedData.some(chain =>
              chain.chainLabel &&
              chain.whys?.length >= 5 &&
              chain.whys.every(why => why.question?.trim() && why.answer?.trim())
            );
            // Classic complete only counts if there are actually filled questions (not empty placeholders)
            const filledClassic = rciData.questions.filter(q => q?.question?.trim() && q?.answer?.trim());
            const hasClassicComplete = filledClassic.length >= 5;
            const hasDescription = hasMinimumContent(props.RootCauseIdentificationState?.description);
            const hasAnyData = hasCompleteChain || hasClassicComplete ||
              hasDescription ||
              chainedData.some(c => c.whys?.some(w => w.question?.trim())) ||
              rciData.questions.some(q => q?.question?.trim() || q?.answer?.trim());

            if (hasCompleteChain || hasClassicComplete || hasDescription) {
              return (
                <span className="sre-section-complete-badge">✓ Complete</span>
              );
            }
            if (!hasAnyData) {
              return (
                <span className="sre-section-header-badge" style={{ flex: 1 }}>
                  Manual entry required
                </span>
              );
            }
            return (
              <span className="sre-section-warning-badge">⚠ Incomplete</span>
            );
          })()}
          <McTooltip position="bottom-right" fit="small" width={340}>
            <McIcon slot="trigger" icon="info-circle" size="16" style={{ cursor: "pointer", color: "#fff" }} />
            <div style={{ fontSize: "12px", lineHeight: "1.6", padding: "4px 0" }}>
              <strong>SRE Best Practices — Root Cause Identification</strong>
              <ul style={{ margin: "6px 0 0", paddingLeft: "16px" }}>
                <li>Use the <strong>5-Why</strong> technique: keep asking "why" until you reach a systemic cause</li>
                <li>Avoid blame — focus on process, tooling, and system failures</li>
                <li>Create multiple chains to explore different root cause paths</li>
                <li>Each "Why" must logically lead to the next — no gaps</li>
                <li>The final "Why" should point to a preventable process or gap</li>
              </ul>
            </div>
          </McTooltip>
          {props.aiInsight && (
            <button
              className={`sre-section-header-action${aiOpen ? " sre-section-header-action--ai-active" : ""}`}
              onClick={() => setAiOpen((v) => !v)}
            >
              <McIcon icon="sparkle" size="12" />
              AI Review
              <McIcon icon={aiOpen ? "chevron-up" : "chevron-down"} size="12" />
            </button>
          )}
          {!props.postmortemIsIncompleteState && (
            <button className="sre-section-header-action" onClick={handleEditClick}>
              <Icon name="pencil" style={{ fontSize: "12px" }} />
              Edit
            </button>
          )}
        </div>

        {/* Body */}
        <div className={aiOpen && props.aiInsight ? "sre-section-body--with-ai" : undefined}>
          <div className={aiOpen && props.aiInsight ? "sre-section-content" : undefined}>
            {(() => {
              const questions = props.RootCauseIdentificationState?.questions || [];
              const descMissing = !hasRealText(props.RootCauseIdentificationState?.description);
              const filledCount = questions.filter((q) => q?.question?.trim() && q?.answer?.trim()).length;
              const needMore = 5 - numberOfQuestions;
              const whysMissing = numberOfQuestions < 5 || filledCount < numberOfQuestions;

              const MissingPill = ({ text }) => (
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: "3px",
                  fontSize: "10px", fontWeight: 700, letterSpacing: "0.04em",
                  color: "#b91c1c", background: "#fee2e2",
                  borderRadius: "8px", padding: "1px 8px",
                  marginLeft: "8px", verticalAlign: "middle", whiteSpace: "nowrap",
                }}>⚠ {text}</span>
              );

              return (
                <>
                  {isEditMode ? (
                    // Edit Mode - Description + Chained 5-Whys
                    <div style={{ background: "#fff", padding: "16px" }}>
                      {/* Description Section */}
                      <div>
                        <div style={{
                          fontSize: "11px",
                          color: "#64748b",
                          marginBottom: "8px",
                          fontStyle: "italic",
                          lineHeight: "1.6"
                        }}>
                          <div>Sample 1. The application had an outage as heap ran out of memory.</div>
                          <div>Sample 2. The heap OOM occurred because there was multiple thread in open state.</div>
                          <div>Sample 3. Open state because we pushed a change to service and didn't expect the threads to not close at end point.</div>
                          <div>Sample 4. Because we didn't perform thorough testing in line with production load.</div>
                          <div>Sample 5. Because we believed Pre-production was near to production and testing gave us sufficient result to push the change to production.</div>
                        </div>
                        <div className="quill-wrapper">
                          <ReactQuill
                            theme="snow"
                            value={rciData.description}
                            onChange={(html) => handleRciData({ target: { name: 'description', value: html } })}
                            modules={{
                              toolbar: [
                                [{ 'header': [1, 2, 3, false] }],
                                ['bold', 'italic', 'underline'],
                                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                ['link'],
                                ['clean']
                              ]
                            }}
                            formats={[
                              'header',
                              'bold', 'italic', 'underline',
                              'list', 'bullet',
                              'link'
                            ]}
                            placeholder="Provide a summary of the root cause analysis..."
                            style={{ background: "#fff" }}
                          />
                        </div>
                        <div style={{ marginTop: "12px", display: "flex", justifyContent: "flex-end" }}>
                          <McButton
                            label="Save Description"
                            appearance="primary"
                            variant="filled"
                            fit="small"
                            click={handleSaveDescription}
                          />
                        </div>
                      </div>

                      {/* 5 Whys Section */}
                      <div style={{ marginTop: "24px" }}>
                        <p className="rci-subsection-header">5 Whys</p>
                        {chainedData.length === 0 && rciData.questions.some(q => q?.question || q?.answer) ? (
                          /* Classic questionnaire editor — MDS-compliant, for postmortems with questionarie[] data */
                          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                            {rciData.questions.map((q, i) => (
                              <div
                                key={i}
                                style={{
                                  border: "1px solid var(--mds_brand_appearance_default_border-color, #e0e0e0)",
                                  borderRadius: "var(--mds_brand_border_medium_radius, 4px)",
                                  overflow: "hidden",
                                }}
                              >
                                {/* WHY strip header */}
                                <div style={{
                                  display: "flex",
                                  alignItems: "center",
                                  padding: "6px 14px",
                                  background: "var(--mds_brand_appearance_neutral_weak_default_background-color, #f5f7f8)",
                                  borderBottom: "1px solid var(--mds_brand_appearance_default_border-color, #e0e0e0)",
                                  gap: "10px",
                                }}>
                                  <span style={{
                                    fontSize: "11px",
                                    fontWeight: "700",
                                    letterSpacing: "0.06em",
                                    color: "var(--mds_brand_appearance_primary_default_text-color, #0073ab)",
                                  }}>
                                    WHY {i + 1}
                                  </span>
                                  {rciData.questions.length > 1 && (
                                    <button
                                      onClick={() => deleteQuestion(i)}
                                      title="Remove this why"
                                      style={{
                                        marginLeft: "auto",
                                        background: "none",
                                        border: "none",
                                        cursor: "pointer",
                                        padding: "2px 4px",
                                        display: "flex",
                                        alignItems: "center",
                                        color: "var(--mds_brand_appearance_default_default_text-color-secondary, #6b6b6b)",
                                      }}
                                    >
                                      <McIcon icon="trash" size="16" />
                                    </button>
                                  )}
                                </div>

                                {/* Fields */}
                                <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: "14px" }}>
                                  <McInput
                                    label="Question"
                                    name="question"
                                    value={q.question || ''}
                                    placeholder={`Why ${i === 0 ? 'did this happen' : 'is that'}?`}
                                    fit="medium"
                                    onInput={(e) => handleRciQuestionaireQuestions(e, i)}
                                  />
                                  <McTextarea
                                    label="Answer (Because…)"
                                    name="answer"
                                    value={q.answer || ''}
                                    placeholder="Because..."
                                    rows={4}
                                    fit="medium"
                                    onInput={(e) => handleRciQuestionaireAnswers(e, i)}
                                    spellcheck="true"
                                    lang="en"
                                  />
                                </div>
                              </div>
                            ))}

                            {/* Action row */}
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "4px" }}>
                              <McButton
                                label={`Add WHY ${rciData.questions.length + 1}`}
                                appearance="neutral"
                                variant="outlined"
                                fit="small"
                                trailingicon="plus"
                                click={increaseQuestions}
                              />
                              <McButton
                                label="Save"
                                appearance="primary"
                                variant="filled"
                                fit="small"
                                click={handleSaveAndExitClassic}
                              />
                            </div>
                          </div>
                        ) : (
                          <ChainedFiveWhys
                            incidentID={props.incidentID}
                            onExitEditMode={handleExitEditMode}
                            onClearAllChains={handleClearAllChains}
                          />
                        )}
                      </div>
                    </div>
                  ) : (
                    // Read-Only Display
                    <div
                      className={`sre-section-body${!props.postmortemIsIncompleteState ? " sre-section-body--editable" : ""}`}
                      style={{ padding: "16px" }}
                      onClick={!props.postmortemIsIncompleteState ? handleEditClick : undefined}
                    >
                      {loadingChainedData ? (
                        <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
                          <LoadingIndicator appearance="default" fit="large" variant="spinner" />
                          <p style={{ marginTop: "16px" }}>Loading chains...</p>
                        </div>
                      ) : (
                        <div>
                          {/* Description */}
                          {props.RootCauseIdentificationState?.description && hasRealText(props.RootCauseIdentificationState.description) && (
                            <div
                              style={{
                                fontSize: "14px",
                                lineHeight: 1.6,
                                color: "var(--mds_brand_appearance_default_default_text-color)",
                                marginBottom: "8px",
                              }}
                              className="chain-description-content"
                              dangerouslySetInnerHTML={{ __html: props.RootCauseIdentificationState.description }}
                            />
                          )}

                          {/* 5 Whys Section Header */}
                          {(chainedData.length > 0 || rciData.questions.some(q => q?.question || q?.answer)) && (
                            <p className="rci-subsection-header">5 Whys</p>
                          )}

                          {/* Chains — McTabBar for multiple, single card for one, classic for legacy */}
                          {chainedData.length > 1 ? (
                            <McTabBar fit="small">
                              {chainedData.map((chain) => [
                                <McTab
                                  key={`tab-${chain.chainId}`}
                                  slot="tab"
                                  label={[chain.chainLetter, chain.chainLabel].filter(Boolean).join(" — ")}
                                />,
                                <div key={`panel-${chain.chainId}`} slot="panel">
                                  {chain.chainDescription && hasRealText(chain.chainDescription) && (
                                    <div style={{
                                      padding: "10px 12px",
                                      fontSize: "12px",
                                      color: "var(--mds_brand_appearance_default_default_text-color-secondary, #6b6b6b)",
                                      lineHeight: 1.5,
                                      borderBottom: "1px solid var(--mds_brand_appearance_default_border-color, #e8e8e8)",
                                    }}>
                                      {chain.chainDescription}
                                    </div>
                                  )}
                                  <div style={{ padding: "0 12px" }}>
                                    {chain.whys && chain.whys.filter(w => w.question && w.answer).map((why, wi, arr) => (
                                      <WhyRow key={wi} label={`WHY ${why.whyNumber}`} question={why.question} answer={why.answer} isLast={wi === arr.length - 1} />
                                    ))}
                                  </div>
                                </div>,
                              ])}
                            </McTabBar>
                          ) : chainedData.length === 1 ? (
                            <div style={{ border: "1px solid var(--mds_brand_appearance_default_border-color, #e8e8e8)", borderRadius: "var(--mds_brand_border_medium_radius, 4px)", overflow: "hidden" }}>
                              {chainedData[0].chainDescription && hasRealText(chainedData[0].chainDescription) && (
                                <div style={{
                                  padding: "10px 12px",
                                  fontSize: "12px",
                                  color: "var(--mds_brand_appearance_default_default_text-color-secondary, #6b6b6b)",
                                  lineHeight: 1.5,
                                  borderBottom: "1px solid var(--mds_brand_appearance_default_border-color, #e8e8e8)",
                                }}>
                                  {chainedData[0].chainDescription}
                                </div>
                              )}
                              <div style={{ padding: "0 12px" }}>
                                {chainedData[0].whys && chainedData[0].whys.filter(w => w.question && w.answer).map((why, wi, arr) => (
                                  <WhyRow key={wi} label={`WHY ${why.whyNumber}`} question={why.question} answer={why.answer} isLast={wi === arr.length - 1} />
                                ))}
                              </div>
                            </div>
                          ) : rciData.questions.some(q => q?.question || q?.answer) ? (
                            /* Classic questionnaire fallback */
                            <div style={{ border: "1px solid var(--mds_brand_appearance_default_border-color, #e8e8e8)", borderRadius: "var(--mds_brand_border_medium_radius, 4px)", overflow: "hidden" }}>
                              <div style={{ padding: "0 12px" }}>
                                {rciData.questions.map((q, i) => (
                                  <WhyRow
                                    key={i}
                                    label={`WHY ${i + 1}`}
                                    question={q.question}
                                    answer={q.answer ? q.answer.replace(/\n/g, "<br/>") : null}
                                    isLast={i === rciData.questions.length - 1}
                                  />
                                ))}
                              </div>
                            </div>
                          ) : (
                            /* True empty state */
                            (!props.RootCauseIdentificationState?.description || !hasRealText(props.RootCauseIdentificationState.description)) && (
                              <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--mds_brand_appearance_default_default_text-color-secondary, #6b6b6b)" }}>
                                <McIcon icon="info-circle" size="32" style={{ marginBottom: "12px", display: "block", margin: "0 auto 12px" }} />
                                <div style={{ fontSize: "14px" }}>Root cause analysis not completed</div>
                                <div style={{ fontSize: "12px", marginTop: "6px" }}>Click "Edit" to add a root cause description or create 5 Whys chains</div>
                              </div>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              );
            })()}

            {/* Image Upload Section */}
            <div
              style={{
                margin: "0 16px 16px",
                border: "1px solid var(--mds_brand_appearance_default_border-color, #e0e0e0)",
                borderRadius: "var(--mds_brand_border_medium_radius, 4px)",
                overflow: "hidden",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 14px",
                background: "var(--mds_brand_appearance_neutral_weak_default_background-color, #f5f7f8)",
                borderBottom: "1px solid var(--mds_brand_appearance_default_border-color, #e0e0e0)",
              }}>
                <span style={{
                  fontSize: "11px",
                  fontWeight: "700",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "var(--mds_brand_appearance_default_default_text-color-secondary, #6b6b6b)",
                }}>
                  Uploaded Images
                </span>
                <McButton
                  label="Add Image"
                  appearance="neutral"
                  variant="outlined"
                  fit="small"
                  leadingicon="camera"
                  click={() => handlePhotoModalClick("rootCauseIdentification")}
                />
              </div>
              <div style={{ padding: "12px 14px" }}>
                {props.loading ? (
                  <Loading />
                ) : (
                  <UploadedImages
                    images={props.uploadedImages ? props.uploadedImages : []}
                    previewImage={previewImage}
                    deleteImage={deleteImage}
                    deleteImageFor="rootCauseIdentification"
                    error={props.error}
                  />
                )}
              </div>
            </div>

            <div style={{ padding: "0 16px" }}>
            {/* Root Cause (cause text) — legacy sub-section, shown only when data exists */}
            {props.rcCodeState?.cause?.replace(/<[^>]*>/g, "").trim() && (
              <RootCauseCauseSection
                cause={props.rcCodeState.cause}
                dispatch={props.dispatch}
                rcCodeState={props.rcCodeState}
                lockedFields={props.lockedFields}
                postmortemIsIncompleteState={props.postmortemIsIncompleteState}
                onAutoSave={props.onAutoSave}
              />
            )}

            {/* Root Cause Code subsection */}
            <div style={{ margin: "16px 0 0" }} onClick={(e) => e.stopPropagation()}>
              {/* subsection header */}
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                fontSize: "11px",
                fontWeight: "700",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "var(--mds_brand_appearance_default_default_text-color-secondary, #6b6b6b)",
                background: "var(--mds_brand_appearance_neutral_weak_default_background-color, #f5f7f8)",
                borderTop: "1px solid var(--mds_brand_appearance_default_border-color, #e0e0e0)",
                borderBottom: "1px solid var(--mds_brand_appearance_default_border-color, #e0e0e0)",
                padding: "8px 16px",
                margin: "0 -16px",
              }}>
                <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  Root Cause Code
                  {props.rcCodeState?.rootCauseCode && (
                    <span style={{
                      fontSize: "10px",
                      fontWeight: "600",
                      color: "#1a8a4a",
                      background: "#e6f4ec",
                      borderRadius: "10px",
                      padding: "1px 8px",
                      letterSpacing: "0.02em",
                      textTransform: "none",
                    }}>✓ Complete</span>
                  )}
                </span>
                {!props.postmortemIsIncompleteState && (
                  <button className="sre-section-header-action" onClick={props.onEditRootCauseCode}
                    style={{ textTransform: "none", letterSpacing: "normal", fontSize: "12px" }}>
                    <Icon name="pencil" style={{ fontSize: "12px" }} />
                    Edit
                  </button>
                )}
              </div>

              {/* subsection body */}
              <div
                className={!props.postmortemIsIncompleteState ? "sre-section-body--editable" : undefined}
                style={{ padding: 0, cursor: !props.postmortemIsIncompleteState ? "pointer" : undefined }}
                onClick={!props.postmortemIsIncompleteState ? props.onEditRootCauseCode : undefined}
              >
                {(() => {
                  const rcState = props.rcCodeState || {};
                  const hasSubCat = !!(rcState.subCategory || rcState.rootCauseSubCategory);
                  const hasTags = rcState.tags?.length > 0;
                  const isAPMT = props.portfolio?.value === "APMT, IOT and Automation";
                  return (
                    <>
                      <div className={hasSubCat || hasTags || isAPMT ? "rc-fields-grid" : "rc-fields-grid rc-fields-grid--2col"}>
                        <div className="rc-field-cell root-cause-cause-code-div-tour-step">
                          <span className="rc-field-label">Root Cause Code</span>
                          <span className="rc-field-value">
                            {rcState.rootCauseCode || <i style={{ color: "#aaa" }}>Not set</i>}
                          </span>
                          <IsLocked field="rc_cause_code" lockedFields={props.lockedFields} graphData={graphData} />
                        </div>
                        <div className="rc-field-cell root-cause-sub-code-div-tour-step">
                          <span className="rc-field-label">Root Cause Sub Code</span>
                          <span className="rc-field-value">
                            {rcState.rootCauseSubCode || <i style={{ color: "#aaa" }}>Not set</i>}
                          </span>
                          <IsLocked field="rc_cause_sub_code" lockedFields={props.lockedFields} graphData={graphData} />
                        </div>
                        {hasSubCat ? (
                          <div className="rc-field-cell root-cause-sub-category-div">
                            <span className="rc-field-label">Sub Category</span>
                            <span className="rc-field-value">
                              {rcState.subCategory || rcState.rootCauseSubCategory}
                            </span>
                            <IsLocked field="rc_sub_category" lockedFields={props.lockedFields} graphData={graphData} />
                          </div>
                        ) : hasTags ? (
                          <div className="rc-field-cell root-cause-tag-div-tour-step">
                            <span className="rc-field-label">Tags</span>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "4px" }}>
                              {rcState.tags.map((tag) => (
                                <span key={tag} className="rc-tag-chip">{tag}</span>
                              ))}
                            </div>
                            <IsLocked field="tags" lockedFields={props.lockedFields} graphData={graphData} />
                          </div>
                        ) : (
                          null
                        )}
                        {isAPMT && (
                          <div className="rc-field-cell">
                            <span className="rc-field-label">MVC Vulnerability Related</span>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "4px" }}>
                              {props.mvcVulnerabilityRelated?.length > 0
                                ? props.mvcVulnerabilityRelated.map((item, idx) => (
                                    <span key={idx} className="rc-tag-chip">{item.value || item.label || item}</span>
                                  ))
                                : <span className="rc-field-value"><i style={{ color: "#aaa" }}>Not set</i></span>}
                            </div>
                          </div>
                        )}
                      </div>
                      {hasSubCat && hasTags && (
                        <div className="rc-field-cell rc-field-cell--full root-cause-tag-div-tour-step">
                          <span className="rc-field-label">Tags</span>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "4px" }}>
                            {rcState.tags.map((tag) => (
                              <span key={tag} className="rc-tag-chip">{tag}</span>
                            ))}
                          </div>
                          <IsLocked field="tags" lockedFields={props.lockedFields} graphData={graphData} />
                        </div>
                      )}
                    </>
                  );
                })()}

                <div className="rc-fields-grid">
                  <div className="rc-field-cell root-cause-vendor-div-tour-step">
                    <span className="rc-field-label">Caused By Vendor</span>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "4px" }}>
                      {(() => {
                        const cv = props.rcCodeState?.causedByVendor;
                        const isYes = cv === "Yes" || cv === "YES" || cv?.value === "Yes" || cv?.value === "YES";
                        const vendorLabel = isYes
                          ? `Yes${props.rcCodeState?.vendorDetails ? ` — ${props.rcCodeState.vendorDetails}` : ""}`
                          : "No";
                        return (
                          <>
                            <span style={{
                              display: "inline-flex", alignItems: "center", justifyContent: "center",
                              width: "18px", height: "18px", borderRadius: "50%", flexShrink: 0,
                              backgroundColor: isYes
                                ? "var(--mds_brand_appearance_error_weak_background-color, #fcdde0)"
                                : "var(--mds_brand_appearance_success_weak_background-color, #d4edda)",
                            }}>
                              <McIcon icon={isYes ? "exclamation-circle" : "check"} size="10"
                                style={{ color: isYes ? "var(--mds_brand_appearance_error_default_on-background-color, #370005)" : "var(--mds_brand_appearance_success_default_on-background-color, #155724)" }} />
                            </span>
                            <span style={{ fontSize: "14px", fontWeight: isYes ? 600 : 400, color: isYes ? "var(--mds_brand_appearance_error_default_text-color, #B80012)" : "#141414" }}>
                              {vendorLabel}
                            </span>
                          </>
                        );
                      })()}
                    </div>
                    <IsLocked field="rc_vendor_details" lockedFields={props.lockedFields} graphData={graphData} />
                  </div>
                  <div className="rc-field-cell root-cause-change-number-div-tour-step">
                    <span className="rc-field-label">Caused By Change</span>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "4px" }}>
                      {(() => {
                        const isYes = props.rcCodeState?.impactedByChange === true || props.rcCodeState?.impactedByChange === "true";
                        const changeLabel = isYes
                          ? `Yes${props.rcCodeState?.causedByChangeNumber ? ` — ${props.rcCodeState.causedByChangeNumber}` : ""}`
                          : "No";
                        return (
                          <>
                            <span style={{
                              display: "inline-flex", alignItems: "center", justifyContent: "center",
                              width: "18px", height: "18px", borderRadius: "50%", flexShrink: 0,
                              backgroundColor: isYes
                                ? "var(--mds_brand_appearance_error_weak_background-color, #fcdde0)"
                                : "var(--mds_brand_appearance_success_weak_background-color, #d4edda)",
                            }}>
                              <McIcon icon={isYes ? "exclamation-circle" : "check"} size="10"
                                style={{ color: isYes ? "var(--mds_brand_appearance_error_default_on-background-color, #370005)" : "var(--mds_brand_appearance_success_default_on-background-color, #155724)" }} />
                            </span>
                            <span style={{ fontSize: "14px", fontWeight: isYes ? 600 : 400, color: isYes ? "var(--mds_brand_appearance_error_default_text-color, #B80012)" : "#141414" }}>
                              {changeLabel}
                            </span>
                          </>
                        );
                      })()}
                    </div>
                    <IsLocked field="rc_change_number" lockedFields={props.lockedFields} graphData={graphData} />
                  </div>
                  <div className="rc-field-cell">
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
                      <span className="rc-field-label" style={{ marginBottom: 0 }}>Recurrence</span>
                      <McTooltip position="bottom-right" fit="small" width={320}>
                        <McIcon slot="trigger" icon="info-circle" size="16" style={{ cursor: "pointer", color: "var(--mds_brand_appearance_primary_default_text-color, #0073ab)" }} />
                        <span>Based on the root cause, review prior incidents to determine if similar root cause had occurred. If yes, plug the preventive and corrective measures for the CI or CMDB of the incident.</span>
                      </McTooltip>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "4px" }}>
                      {(() => {
                        const rv = props.rcRecurrenceState?.recurringIssue;
                        const isYes = rv && rv !== "No" && rv !== "no" && rv !== "false" && rv !== false;
                        const references = props.rcRecurrenceState?.pastReferencesList?.length
                          ? props.rcRecurrenceState.pastReferencesList
                          : [{ incidentId: props.rcRecurrenceState?.incidentId, problemId: props.rcRecurrenceState?.problemId }];
                        const detail = references
                          .map((reference) => [reference.incidentId, reference.problemId].filter(Boolean).join(" / "))
                          .filter(Boolean)
                          .join(", ");
                        const recurrenceLabel = isYes ? `Yes${detail ? ` — ${detail}` : ""}` : "No";
                        return (
                          <>
                            <span style={{
                              display: "inline-flex", alignItems: "center", justifyContent: "center",
                              width: "18px", height: "18px", borderRadius: "50%", flexShrink: 0,
                              backgroundColor: isYes
                                ? "var(--mds_brand_appearance_error_weak_background-color, #fcdde0)"
                                : "var(--mds_brand_appearance_success_weak_background-color, #d4edda)",
                            }}>
                              <McIcon icon={isYes ? "exclamation-circle" : "check"} size="10"
                                style={{ color: isYes ? "var(--mds_brand_appearance_error_default_on-background-color, #370005)" : "var(--mds_brand_appearance_success_default_on-background-color, #155724)" }} />
                            </span>
                            <span style={{ fontSize: "14px", fontWeight: isYes ? 600 : 400, color: isYes ? "var(--mds_brand_appearance_error_default_text-color, #B80012)" : "#141414" }}>
                              {recurrenceLabel}
                            </span>
                          </>
                        );
                      })()}
                    </div>
                    <IsLocked field="recurrence" lockedFields={props.lockedFields} graphData={graphData} />
                  </div>
                </div>
              </div>{/* subsection body */}
            </div>{/* Root Cause Code subsection */}
            </div>{/* padding wrapper for sub-sections */}

          </div>{/* sre-section-content */}
          {aiOpen && props.aiInsight && (
            <div className="sre-section-ai-col" onClick={e => e.stopPropagation()}>
              <AiSuggestionsInline insight={props.aiInsight} sectionName="Root Cause" dim={props.aiDim} />
            </div>
          )}
        </div>{/* sre-section-body--with-ai wrapper */}
      </div>

      <IsLocked
        field="rci_questions"
        lockedFields={props.lockedFields}
        graphData={graphData}
      />

      <RciModal
        handleRciModal={handleRciModal}
        rciModal={rciModal}
        rciData={rciData}
        handleRciData={handleRciData}
        increaseQuestions={increaseQuestions}
        numberOfQuestions={numberOfQuestions}
        handleRciQuestionaireQuestions={handleRciQuestionaireQuestions}
        handleRciQuestionaireAnswers={handleRciQuestionaireAnswers}
        deleteQuestion={deleteQuestion}
        onSave={onSave}
        lockedFields={props.lockedFields}
        savingRef={rciSavingRef}
        incidentID={props.incidentID}
      />

      {/* Photo Upload Modal */}
      <PhotoUploadModal
        modalOpen={photoModal}
        handleModal={handlePhotoModalClick}
        handleModalClose={handlePhotoModalClose}
        files={props.images.rootCause.rootCauseIdentification}
        handleFiles={handleFiles}
        fileChange={fileChangeWithModalClose}
        hanldePreviewModal={hanldePreviewModal}
        imageToPreview={imageToPreview}
        previewModal={previewModal}
        error={error}
        errorMessage={errorMessage}
        photoFor={photosFor}
      />

      {/* Photo Preview Modal */}
      <PhotoPreviewModal
        imageToPreview={imageToPreview}
        previewModal={previewModal}
        hanldePreviewModal={hanldePreviewModal}
      />
    </>
  );
}

export default RootCauseIdentification;


